import React, { useEffect, useState } from "react";
import DataTable from "../DataTable";
import { getEventsByUserId, getCurrentUserDetails, getAllEvents } from "../utils";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Row, Col, Spinner } from "react-bootstrap";
import { FaMoneyCheckAlt, FaCalendarAlt } from "react-icons/fa";

function getDateOnly(date) {
  if (!date) return null;
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export default function AllEvents() {
  const auth = useAuth();
  const currentUser = auth.currentUser;
  const [events, setEvents] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const userDetails = await getCurrentUserDetails();
        setUserRole(userDetails.role);
        let fetchedEvents = [];
        if (userDetails.role === "Boat Owner" || userDetails.role === "Renter") {
          fetchedEvents = await getEventsByUserId(currentUser.uid);
        } else if (userDetails.role === "Admin") {
          fetchedEvents = await getAllEvents(currentUser.uid);
        }
        setEvents(fetchedEvents || []);
      } catch (error) {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [currentUser]);

  // Prepare columns for DataTable
  const columns = [
    { Header: "Event ID", accessor: "id" },
    { Header: "Title", accessor: "title" },
    { Header: "Start", accessor: "start" },
    { Header: "End", accessor: "end" },
    {
      Header: "Offered Price",
      accessor: "offerPrice",
      Cell: ({ value }) => (value && value !== "-" ? `$${value}` : "-"),
    },
    { Header: "Duration", accessor: "duration" },
    { Header: "Guests", accessor: "guests" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => {
        const status = row.original.status;
        const color = row.original.color;
        return <span style={{ color: color, fontWeight: 500, fontSize: 14, textTransform: "capitalize" }}>{status}</span>;
      },
    },
    {
      Header: "View",
      id: "view",
      Cell: ({ row }) => (
        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            if (row.original.id && row.original.id !== "undefined") {
              navigate(`/dashboard/event/${row.original.id}`);
            }
          }}
          disabled={!row.original.id || row.original.id === "undefined"}
        >
          View
        </button>
      ),
    },
  ];

  // Filter out events with undefined or 'undefined' id
  const filteredEvents = events.filter(event => event.id && event.id !== "undefined");

  // Prepare data for DataTable
  const data = filteredEvents.map((event) => {
    const startDate = event.start ? new Date(event.start) : (event.inquireInfo?.offerData?.offeredDate ? new Date(event.inquireInfo.offerData.offeredDate) : null);
    return {
      id: event.id,
      title: event.title,
      start: event.start ? new Date(event.start).toLocaleString() : "-",
      end: event.end ? new Date(event.end).toLocaleString() : "-",
      offerPrice: event.inquireInfo?.offerData?.offerPrice ?? "-",
      duration: event.inquireInfo?.offerData?.duration ?? "-",
      guests: event.inquireInfo?.inquiryData?.total ?? "-",
      status: event.inquireInfo?.status ?? "-",
      color: event.color,
      _sortDate: startDate ? startDate.getTime() : 0,
      _dateOnly: startDate ? getDateOnly(startDate).getTime() : 0,
    };
  });

  // Sort: first by dateOnly (descending), then by time (descending)
  const sortedData = data.sort((a, b) => {
    if (b._dateOnly !== a._dateOnly) {
      return b._dateOnly - a._dateOnly;
    }
    return b._sortDate - a._sortDate;
  });

  // Calculate totals
  const totalEvents = sortedData.length;
  const totalOfferedPrice = sortedData.reduce((sum, event) => {
    const price = parseFloat(event.offerPrice && event.offerPrice !== "-" ? event.offerPrice : 0);
    return sum + (isNaN(price) ? 0 : price);
  }, 0);

  return (
    <>
      <div className="row">
        <div className="col">
          <div className="page-description">
            <h1>All Bookings</h1>
          </div>
        </div>
      </div>
      <Row>
        <Col xs={12} md={6}>
          <div className="card widget widget-bank-card">
            <div className="card-body">
              <div className="widget-bank-card-container widget-bank-card-mastercard d-flex flex-column">
                <div className="card-logo">
                  <FaCalendarAlt style={{ width: "50px", height: "50px" }} />
                </div>
                <span className="widget-bank-card-balance-title">Total Events</span>
                <span className="widget-bank-card-balance">{totalEvents}</span>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="card widget widget-bank-card">
            <div className="card-body">
              <div className="widget-bank-card-container widget-bank-card-visa d-flex flex-column">
                <div className="card-logo">
                  <FaMoneyCheckAlt style={{ width: "50px", height: "50px", color: "white" }} />
                </div>
                <span className="widget-bank-card-balance-title">Total Offered Price</span>
                <span className="widget-bank-card-balance">${totalOfferedPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <div className="row">
        <div className="col-12">
          <div className="card calendar-container">
            <div className="card-body">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <DataTable columns={columns} data={sortedData} onRowClick={() => {}} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
} 